<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUsersStore } from '../stores/users.js'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { fetchAllOrganizationsRequest } from '../services/organizationService.js'
import { getAssignableRoles } from '../utils/permissions.js'
import { awaitScopedContextReady } from '../utils/contextReady.js'
import { canModifyOrg, canModifySubOrg } from '../utils/lifecycleUtils.js'

const emit = defineEmits(['created'])
const usersStore = useUsersStore()
const authStore = useAuthStore()
const orgStore = useOrgStore()

const username = ref('')
const password = ref('')
const email = ref('')
const phone = ref('')
const formError = ref('')
const selectedOrganizationId = ref('')
const selectedSubOrgId = ref('')
const selectedRole = ref('')
const allOrganizations = ref([])
const contextReady = ref(false)
const assignableRoles = computed(() => getAssignableRoles(orgStore.activeOrgRole))

const syncSelectedRole = () => {
  if (assignableRoles.value.includes(selectedRole.value)) {
    return
  }
  selectedRole.value = assignableRoles.value[0] ?? ''
}

const availableOrganizations = computed(() => {
  if (authStore.isSuperAdmin) {
    return allOrganizations.value
  }
  return orgStore.organizations
})

const assignableOrganizations = computed(() => {
  return availableOrganizations.value.filter((org) => canModifyOrg(org))
})

const selectedOrganization = computed(() => {
  const organizationId = Number(selectedOrganizationId.value)
  if (!Number.isInteger(organizationId) || organizationId <= 0) return null
  return availableOrganizations.value.find((org) => Number(org?.id) === organizationId) ?? null
})

const selectedOrganizationReadOnly = computed(() => {
  return Boolean(selectedOrganization.value) && !canModifyOrg(selectedOrganization.value)
})

const availableSubOrgs = computed(() => {
  const organizationId = Number(selectedOrganizationId.value)
  if (!Number.isInteger(organizationId) || organizationId <= 0) {
    return []
  }

  return (orgStore.subOrgs ?? []).filter(
    (subOrg) => Number(subOrg?.organization_id) === organizationId,
  )
})

const assignableSubOrgs = computed(() => {
  return availableSubOrgs.value.filter((subOrg) => canModifySubOrg(subOrg))
})

const selectedSubOrg = computed(() => {
  const subOrgId = Number(selectedSubOrgId.value)
  if (!Number.isInteger(subOrgId) || subOrgId <= 0) return null
  return availableSubOrgs.value.find((subOrg) => Number(subOrg?.id) === subOrgId) ?? null
})

const selectedSubOrgReadOnly = computed(() => {
  return Boolean(selectedSubOrg.value) && !canModifySubOrg(selectedSubOrg.value)
})

const isSubmitDisabled = computed(() => {
  return (
    usersStore.loading
    || !contextReady.value
    || !username.value.trim()
    || !password.value
    || !email.value.trim()
    || !phone.value.trim()
    || !selectedOrganizationId.value
    || !selectedSubOrgId.value
    || !selectedRole.value
    || selectedOrganizationReadOnly.value
    || selectedSubOrgReadOnly.value
  )
})

const loadOrganizations = async () => {
  try {
    if (authStore.isSuperAdmin) {
      const data = await fetchAllOrganizationsRequest()
      allOrganizations.value = Array.isArray(data) ? data : []
      return
    }

    await orgStore.fetchMyOrganizations()
  } catch {
    allOrganizations.value = []
  }
}

watch(
  () => selectedOrganizationId.value,
  (nextOrganizationId, previousOrganizationId) => {
    if (nextOrganizationId === previousOrganizationId) return
    selectedSubOrgId.value = ''
  },
)

onMounted(() => {
  ;(async () => {
    contextReady.value = await awaitScopedContextReady(authStore, orgStore)
    if (!contextReady.value) return

    await loadOrganizations()
    await orgStore.fetchSubOrgs()

    if (!selectedOrganizationId.value && orgStore.activeOrgId) {
      selectedOrganizationId.value = String(orgStore.activeOrgId)
    }

    if (!selectedSubOrgId.value && orgStore.activeSubOrgId) {
      selectedSubOrgId.value = String(orgStore.activeSubOrgId)
    }

    syncSelectedRole()
  })()
})

watch(
  () => orgStore.activeOrgRole,
  () => {
    syncSelectedRole()
  },
  { immediate: true },
)

const submit = async () => {
  formError.value = ''

  const organizationId = Number(selectedOrganizationId.value)
  if (!Number.isInteger(organizationId) || organizationId <= 0) {
    formError.value = 'Organization must be selected.'
    return
  }

  if (selectedOrganizationReadOnly.value) {
    formError.value = 'Cannot assign users to a deleted organization.'
    return
  }

  if (!selectedRole.value) {
    formError.value = 'Role must be selected.'
    return
  }

  const subOrgId = Number(selectedSubOrgId.value)
  if (!Number.isInteger(subOrgId) || subOrgId <= 0) {
    formError.value = 'Sub-organization is required'
    return
  }

  if (selectedSubOrgReadOnly.value) {
    formError.value = 'Cannot assign users to a deleted sub-organization.'
    return
  }

  const payload = {
    username: username.value.trim(),
    password: password.value,
    email: email.value.trim(),
    phone: phone.value.trim(),
    role: selectedRole.value,
    organization_id: organizationId,
    sub_org_id: subOrgId,
  }

  const result = await usersStore.createUser(payload)

  if (!result.success) {
    formError.value = result.error
    return
  }

  username.value = ''
  password.value = ''
  email.value = ''
  phone.value = ''
  selectedOrganizationId.value = ''
  selectedSubOrgId.value = ''
  selectedRole.value = assignableRoles.value[0] ?? ''
  emit('created', result.user)
}
</script>

<template>
  <div class="create-user-form">
    <h3>Create User</h3>
    <p v-if="!contextReady">Loading organization context...</p>
    <form v-else @submit.prevent="submit">
      <label class="form-label">Username<input v-model="username" required type="text" class="form-control" /></label>
      <label class="form-label">Password<input v-model="password" required type="password" minlength="6" class="form-control" /></label>
      <label class="form-label">Email<input v-model="email" required type="email" class="form-control" /></label>
      <label class="form-label">Phone<input v-model="phone" required type="text" class="form-control" /></label>

      <label class="form-label">
        Organization
        <select v-model="selectedOrganizationId" required class="form-select">
          <option disabled value="">Select organization</option>
          <option v-for="org in assignableOrganizations" :key="org.id" :value="org.id">
            {{ org.name }}
          </option>
        </select>
      </label>

      <p v-if="selectedOrganizationReadOnly" class="error">Selected organization is deleted and read-only.</p>

      <label class="form-label">
        SubOrg
        <select v-model="selectedSubOrgId" required class="form-select">
          <option disabled value="">Select sub-organization</option>
          <option v-for="subOrg in assignableSubOrgs" :key="subOrg.id" :value="subOrg.id">
            {{ subOrg.name }}
          </option>
        </select>
      </label>

      <p v-if="!selectedSubOrgId" class="error">Sub-organization is required</p>
      <p v-if="selectedSubOrgReadOnly" class="error">Selected sub-organization is deleted and read-only.</p>

      <label class="form-label">
        Role
        <select v-model="selectedRole" required class="form-select" :disabled="assignableRoles.length === 0">
          <option disabled value="">Select role</option>
          <option v-for="userRole in assignableRoles" :key="userRole" :value="userRole">
            {{ userRole }}
          </option>
        </select>
      </label>

      <p v-if="assignableRoles.length === 0" class="error">You do not have permission to create users with assignable roles.</p>

      <button type="submit" class="btn btn-primary" :disabled="isSubmitDisabled">
        {{ usersStore.loading ? 'Creating...' : 'Create user' }}
      </button>

      <p v-if="formError" class="error">{{ formError }}</p>
    </form>
  </div>
</template>

<style scoped>
.create-user-form {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

form {
  display: grid;
  gap: 0.6rem;
}

.error {
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.45rem;
}

</style>

